// src/address-lookup/index.ts
var BASE_URL = "https://api.addressy.com/Capture/Interactive";
var FIND_URL = `${BASE_URL}/Find/v1.00/json3.ws`;
var RETRIEVE_URL = `${BASE_URL}/Retrieve/v1.2/json3.ws`;
var COUNTRIES = ["GB"];
var LIMIT = 20;
var AddressLookup = class _AddressLookup {
  constructor({ apiKey, limit, countries }) {
    this.apiKey = apiKey;
    this.limit = limit || LIMIT;
    this.countries = countries || COUNTRIES;
    const countriesParam = this.countries.join(",");
    this.findUrl = `${FIND_URL}?Key=${this.apiKey}&Limit=${this.limit}&Countries=${countriesParam}`;
    this.retrieveUrl = `${RETRIEVE_URL}?Key=${this.apiKey}`;
  }
  static async fetchFromApi(url, handleResponse) {
    return new Promise((resolve, reject) => {
      fetch(url).then((response) => response.json()).then((data) => resolve(handleResponse(data))).catch((error) => reject(Error(String(error))));
    });
  }
  find(partialAddress) {
    return new Promise((resolve, reject) => {
      fetch(`${this.findUrl}&Text=${partialAddress}`).then((response) => response.json()).then((data) => resolve(this.formatItems(data))).catch((error) => reject(Error(String(error))));
    });
  }
  findById(id) {
    return new Promise((resolve, reject) => {
      fetch(`${this.findUrl}&Container=${id}`).then((response) => response.json()).then((data) => resolve(this.formatItems(data))).catch((error) => reject(Error(String(error))));
    });
  }
  get(id) {
    return new Promise((resolve, reject) => {
      fetch(`${this.retrieveUrl}&Id=${id}`).then((response) => response.json()).then((data) => resolve(_AddressLookup.formatUkAddress(data))).catch((error) => reject(Error(String(error))));
    });
  }
  formatItems({ Items }) {
    if (!Items || !Array.isArray(Items)) {
      throw Error("No address found");
    }
    if (Items[0].Error) {
      throw Error(Items[0].Description);
    }
    return Items.map(
      (item) => ({
        id: item.Id,
        label: `${item.Text}, ${item.Description}`,
        value: item.Text,
        // Add a callback to trigger secondary search
        // if the address type is not "Address"
        callback: item.Type === "Address" ? void 0 : () => this.findById(item.Id)
      })
    );
  }
  static formatUkAddress({ Items }) {
    if (!Items || !Array.isArray(Items)) {
      throw Error("No address found");
    }
    if (Items[0].Error) {
      throw Error(Items[0].Description);
    }
    const { Line1, Line2, City, PostalCode } = Items[0];
    return {
      address_1: Line1,
      address_2: Line2,
      town: City,
      uk_postcode: PostalCode
    };
  }
};

// src/airbrake/index.ts
import { Notifier } from "@airbrake/browser";
var defaultProjectId = 512949;
var defaultProjectKey = "4e25197d8faea61c10fbb97702200780";
var notifierInstance;
var fakeNotify = () => {
  if (false) {
    return jest.fn();
  }
  return console.error;
};
var fakeAirbrake = {
  notify: fakeNotify()
};
var shouldUseAirbrake = () => {
  const { location } = globalThis;
  const isTest = false;
  return isTest || location?.hostname.indexOf("quote.") > -1;
};
function getAirbrake(options) {
  const { projectId, projectKey, forceSend } = options;
  if (!forceSend || !shouldUseAirbrake()) {
    return fakeAirbrake;
  }
  if (!notifierInstance) {
    notifierInstance = new Notifier({
      projectId: projectId ?? defaultProjectId,
      projectKey: projectKey ?? defaultProjectKey,
      environment: "development",
      instrumentation: {
        onerror: false
      }
    });
  }
  return notifierInstance;
}

// src/utils/text.ts
var snakeCaseKeys = (object) => Object.entries(object || {}).reduce(
  (acc, [key, value]) => {
    const newKey = camelToSnakeCase(key);
    if (Array.isArray(value) && value.every(isObject)) {
      return {
        ...acc,
        [newKey]: value.map((v) => snakeCaseKeys(v))
      };
    }
    if (isObject(value)) {
      return {
        ...acc,
        [newKey]: snakeCaseKeys(value)
      };
    }
    return {
      ...acc,
      [newKey]: value
    };
  },
  {}
);
var snakeCase = (text = "") => text.toLowerCase().replace(/ /g, "_");
var camelToSnakeCase = (text) => text.charAt(0).toLowerCase() + text.slice(1).replace(
  /(\[.*?\])|[A-Z]/g,
  (match, group) => group ? match : `_${match.toLowerCase()}`
);

// src/utils/isObject.tsx
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
}

// src/snowplow/contexts.ts
var getContexts = (config) => {
  const contextEntries = Object.entries(config || {}).filter(([key]) => key.includes("Context") && key !== "includeGAContext").reduce((acc, [key, value]) => {
    if (typeof value === "object") {
      return { ...acc, [key]: snakeCaseKeys({ ...value }) };
    }
    return {
      ...acc,
      [key]: snakeCaseKeys({ data: { service_channel_identifier: value } })
    };
  }, {});
  return contextEntries;
};
var updateIdentityContext = (contexts, uid) => {
  const index = Object.keys(contexts).findIndex(
    (ctx) => contexts[ctx].schema?.includes("identity_context")
  );
  if (index > -1) {
    const key = Object.keys(contexts)[index];
    return {
      ...contexts,
      [key]: {
        ...contexts[key],
        data: {
          ...contexts[key].data,
          domain_userid: uid
        }
      }
    };
  }
  return contexts;
};
var makeContexts = (keys, config) => (keys || []).map((key) => config[key]);

// src/snowplow/event-definitions/base.ts
var baseEventDefinitions = [
  // Mobile link in header
  {
    name: "mobileLinkClick",
    type: "structured",
    makePayload: () => ({
      category: "marketing",
      action: "link-click",
      label: "mobile_call_button",
      property: window.location.href
    })
  },
  // Operating hours link in footer
  {
    name: "operatingHoursClick",
    type: "unstructured",
    makePayload: () => ({
      schema: "iglu:com.snowplowanalytics.snowplow/unstruct_event/jsonschema/1-0-0",
      data: {
        schema: "iglu:com.simplybusiness/operating_hours_clicked/jsonschema/1-0-2",
        data: {}
      }
    })
  },
  {
    name: "progressbarClicked",
    type: "unstructured",
    makePayload: (params) => {
      const {
        section,
        context,
        fromStepIndex,
        fromStepName,
        stepIndex,
        stepName
      } = params;
      return {
        schema: "iglu:com.simplybusiness/progressbar_clicked/jsonschema/1-1-0",
        data: {
          source_page_index: fromStepIndex,
          source_page_name: fromStepName,
          page_index: stepIndex,
          page_name: stepName,
          site: context.site,
          section_name: section,
          vertical: context.vertical
        }
      };
    }
  },
  // Zendesk help chat link
  {
    name: "helpChatLinkClicked",
    type: "structured",
    makePayload: (params) => {
      const { label } = params;
      return {
        category: "help chat for question",
        action: "help_chat_for_question_clicked",
        label,
        property: window.location.href
      };
    },
    contexts: ["serviceChannelContext"]
  }
];

// src/snowplow/event-definitions/intervention.ts
var interventionEventDefinitions = [
  {
    name: "interventionInteractionCompleted",
    type: "unstructured",
    makePayload: (params) => {
      const {
        interventionName,
        userUniqueIdentifier,
        userUniqueIdentifierType,
        action,
        site
      } = params;
      return {
        schema: "iglu:com.simplybusiness/intervention_interaction_completed/jsonschema/2-1-0",
        data: {
          intervention_name: interventionName,
          user_unique_identifier: userUniqueIdentifier,
          user_unique_identifier_type: userUniqueIdentifierType,
          action,
          site
        }
      };
    }
  }
];

// src/snowplow/constants.ts
var DELIMITER = "|";

// src/snowplow/event-definitions/questionnaire/redaction.ts
var PII_ANSWER = [
  "customer_title",
  "customer_first_name",
  "customer_last_name",
  "customer_email_address",
  "preapp_email_address",
  "customer_telephone_number",
  "customer_alternative_telephone_number",
  "uk_address_lookup",
  "uk_correspondence_address_lookup",
  "customer_address_1_pre_quote",
  "customer_address_2_pre_quote",
  "customer_town_pre_quote",
  "customer_postcode_pre_quote",
  "customer_address_1",
  "customer_address_2",
  "customer_town",
  "customer_postcode",
  "address_1_pre_quote",
  "address_2_pre_quote",
  "uk_postcode_pre_quote",
  "town_pre_quote",
  "owner_first_name",
  "owner_last_name",
  "beneficiary_first_name",
  "beneficiary_last_name",
  "gender",
  "dynamic_ssn_fein",
  "ssn",
  "fein",
  "sof_county_court_judgement_online_yes",
  "sof_county_court_judgement_online_no",
  "sof_ccj_count",
  "sof_ccj_value",
  "sof_ccj_date",
  "sof_court_judgement_yes",
  "sof_court_judgement_no",
  "ccj_date",
  "sof_single_ccj_no",
  "sof_single_ccj_yes",
  "sof_ccj_yes",
  "sof_ccj_no",
  "ccj_count",
  "sof_bankrupt_yes",
  "sof_bankrupt_no",
  "sof_multiple_bankruptcies_yes",
  "sof_multiple_bankruptcies_no",
  "bankruptcy_date",
  "sof_single_bankruptcy_no",
  "sof_single_bankruptcy_yes",
  "sof_bankruptcy_yes",
  "sof_bankruptcy_no",
  "bankruptcy_discharged",
  "sof_liquidation_yes",
  "sof_liquidation_no",
  "sof_multiple_liquidations",
  "liquidation_date",
  "sof_criminal_offence_yes",
  "sof_criminal_offence_no",
  "sof_criminal_yes",
  "sof_criminal_no",
  "hcp_date_of_birth_1",
  "hcp_date_of_birth_2",
  "hcp_date_of_birth_3",
  "hcp_date_of_birth_4",
  "hcp_date_of_birth_5",
  "hcp_date_of_birth_6",
  "hcp_date_of_birth_7",
  "hcp_date_of_birth_8",
  "hcp_date_of_birth_9",
  "hcp_date_of_birth_10",
  "sof_previous_claims_yes",
  "sof_previous_claims_no",
  "sof_claims_yes",
  "sof_claims_no",
  "sof_property_claims_in_the_last_5_years_yes",
  "sof_property_claims_in_the_last_5_years_no",
  "sof_property_claims_in_the_last_0_to_4_years_yes",
  "sof_property_claims_in_the_last_0_to_4_years_no",
  "claim_count",
  "sof_number_of_claims",
  "number_of_claims_in_last_5_years",
  "number_of_claims_in_time_owned",
  "date_of_incident_1",
  "date_of_incident_2",
  "date_of_incident_3",
  "date_of_incident_4",
  "date_of_incident_5",
  "type_of_incident_1",
  "type_of_incident_2",
  "type_of_incident_3",
  "type_of_incident_4",
  "type_of_incident_5",
  "incident_value_1",
  "incident_value_2",
  "incident_value_3",
  "incident_value_4",
  "incident_value_5",
  "sof_pollution_disease_claims_yes",
  "sof_pollution_disease_claims_no",
  "sof_potential_claims_yes",
  "sof_potential_claims_no",
  "sof_previous_circumstances_yes",
  "sof_previous_circumstances_no",
  "sof_possible_claims_yes",
  "sof_possible_claims_no",
  "date_of_possible_incident",
  "type_of_possible_incident",
  "possible_incident_value",
  "sof_property_claims_yes",
  "sof_property_claims_no",
  "sof_liability_claims_yes",
  "sof_liability_claims_no",
  "van_registration",
  "sof_disqualified_director_yes",
  "sof_disqualified_director_no",
  "sof_insurance_refused_yes",
  "sof_insurance_refused_no",
  "sof_insurance_declined_yes",
  "sof_insurance_declined_no",
  "insurance_refused"
];
var RADIO_ANSWER_FROM_QUESTIONS = [
  "have_secondary_trade_yes",
  "have_secondary_trade_no"
];
var ANSWER_SUFFIX_PATTERN = /_(yes|no)$/;
var redact = (params) => {
  let { question, answer } = params;
  const piiAnswer = new Set(PII_ANSWER);
  const radioAnswerFromQuestions = new Set(RADIO_ANSWER_FROM_QUESTIONS);
  if (piiAnswer.has(question)) {
    answer = "REDACTED";
    question = question?.replace(ANSWER_SUFFIX_PATTERN, "");
  } else if (radioAnswerFromQuestions.has(question)) {
    question = question?.replace(ANSWER_SUFFIX_PATTERN, "");
  }
  return { question, answer };
};

// src/snowplow/event-definitions/personalised_cover.ts
var personalisedCoverEventDefinitions = [
  {
    name: "seeMoreClicked",
    type: "structured",
    makePayload: (_) => ({
      category: "personalised_cover",
      action: "see_more_clicked",
      label: "page",
      property: "personalised_cover"
    })
  },
  {
    name: "selectPackageClicked",
    type: "structured",
    makePayload: (_) => ({
      category: "personalised_cover",
      action: "select_package_clicked",
      label: "page",
      property: "personalised_cover"
    })
  },
  {
    name: "validationShown",
    type: "structured",
    makePayload: (params) => {
      const { cover, question } = params;
      return {
        category: "personalised_cover",
        action: "validation_shown",
        label: "cover_question",
        property: `${snakeCase(cover)}_${snakeCase(question)}`
      };
    }
  },
  {
    name: "coverQuestionAnswered",
    type: "unstructured",
    makePayload: (params) => {
      const { context } = params;
      const { question, answer } = redact(params);
      const flatAnswer = Array.isArray(answer) ? answer.join(DELIMITER) : answer;
      return {
        schema: "iglu:com.simplybusiness/form_question_answered/jsonschema/1-0-1",
        data: {
          site: context.site,
          page_name: "personalised_cover",
          section_name: "Personalised Cover Questions",
          question,
          answer: flatAnswer
        }
      };
    }
  },
  {
    name: "limitLinkClicked",
    type: "structured",
    makePayload: (params) => {
      const { label } = params;
      return {
        category: "personalised_cover",
        action: "limit_link_clicked",
        label,
        property: "link_clicked"
      };
    }
  },
  {
    name: "limitChanged",
    type: "structured",
    makePayload: (params) => {
      const { label, limit } = params;
      return {
        category: "personalised_cover",
        action: "limit_changed",
        label,
        property: limit
      };
    }
  },
  {
    name: "coverPresented",
    type: "structured",
    makePayload: (params) => {
      const { property } = params;
      return {
        category: "personalised_cover",
        action: "cover_presented",
        label: "cover_name",
        property
      };
    }
  },
  {
    name: "additionalQuestionsClicked",
    type: "structured",
    makePayload: (params) => {
      const { property } = params;
      return {
        category: "personalised_cover",
        action: "additional_questions_clicked",
        label: "cover_name",
        property
      };
    }
  },
  {
    name: "chatbotPromptClicked",
    type: "structured",
    makePayload: (params) => {
      const { label, property } = params;
      return {
        category: "personalised_cover",
        action: "chatbot_prompt_clicked",
        label: property,
        property: label
      };
    }
  }
];

// src/snowplow/event-definitions/qcp.ts
var qcpEventDefinitions = [
  {
    // QDP details button
    name: "detailsClicked",
    type: "structured",
    makePayload: () => ({
      category: "comparison_cta",
      action: "link_click",
      label: "Details"
    })
  },
  {
    // Buy button
    name: "selectClicked",
    type: "structured",
    makePayload: () => ({
      category: "comparison_cta",
      action: "link_click",
      label: "Buy"
    })
  },
  {
    // Quote Details Slider Next steps button
    name: "nextStepsClicked",
    type: "structured",
    makePayload: () => ({
      category: "quote_details_slider_next_step_cta",
      action: "link_click",
      label: "Next steps"
    })
  },
  {
    // Toggle deductibles accordion
    name: "deductiblesClicked",
    type: "structured",
    makePayload: () => ({
      category: "us-qcp-react",
      action: "view_deductables_clicked",
      label: "view_deductables_clicked",
      property: window.location.href
    })
  },
  {
    name: "deductiblesClickedUk",
    type: "structured",
    makePayload: (params) => {
      const { label, deviceType } = params;
      const urlFriendlyLabel = label.replace(/ /g, "-").toLowerCase();
      return {
        category: `uk-qcp-react-${deviceType}-${urlFriendlyLabel}-view-excess-toggle`,
        action: "view_excess_clicked",
        label,
        property: window.location.href
      };
    }
  },
  {
    // Quote Details Slider opened
    name: "sliderOpened",
    type: "structured",
    makePayload: (params) => {
      const { label } = params;
      return {
        category: "comparison_cta",
        action: "quote_details_slider_opened",
        label
      };
    }
  },
  {
    // Coverage modal opened
    name: "coverageModalOpened",
    type: "structured",
    makePayload: (params) => {
      const {
        category,
        product = "extra_coverage",
        title
      } = params;
      const productLabel = snakeCase(product);
      return {
        category,
        action: `${productLabel}_${snakeCase(title)}_popup_opened`,
        label: productLabel,
        property: window.location.href
      };
    }
  },
  {
    // Toggle cover select
    name: "coverChanged",
    type: "unstructured",
    makePayload: (params) => {
      const { name = "", fromValue = "", toValue = "" } = params;
      let action = "change";
      if (fromValue === "0") {
        action = "add";
      }
      if (toValue === "0") {
        action = "remove";
      }
      return {
        schema: "iglu:com.simplybusiness/comparison_page_cover_changed/jsonschema/1-0-0",
        data: {
          name,
          action,
          from_value: fromValue,
          to_value: toValue
        }
      };
    },
    contexts: ["distributionChannelContext"]
  },
  {
    name: "ratingsModalOpened",
    type: "structured",
    makePayload: (params) => {
      const { category, label } = params;
      return {
        category,
        action: "insurer_rating_help_popup_triggered",
        label,
        property: window.location.href
      };
    }
  },
  {
    name: "coverToggleOpened",
    type: "structured",
    makePayload: () => ({
      category: "qcp_limit_interaction",
      label: "limit_interaction",
      action: "limit_interaction_clicked",
      property: window.location.href
    })
  },
  {
    name: "paymentToggleClicked",
    type: "structured",
    makePayload: (params) => {
      const { category, label } = params;
      return {
        category,
        action: "button_click",
        label,
        property: window.location.href
      };
    }
  },
  {
    name: "insurerDetailsAccordionClicked",
    type: "structured",
    makePayload: () => ({
      category: "quote_details_slider_insurer_details_description",
      action: "accordion_clicked",
      label: "accordion_clicked",
      property: window.location.href
    })
  },
  {
    name: "priceDetailsPopUpOpened",
    type: "structured",
    makePayload: () => ({
      category: "price_details",
      action: "price_details_popup_opened",
      label: "Price details",
      property: window.location.href
    })
  },
  {
    name: "editLimitButtonClicked",
    type: "structured",
    makePayload: () => ({
      category: "cover_limit_changes",
      action: "edit_limit_button_clicked",
      label: "Edit Limit",
      property: window.location.href
    })
  },
  {
    name: "applyButtonClicked",
    type: "structured",
    makePayload: () => ({
      category: "cover_limit_changes",
      action: "apply_button_clicked",
      label: "Apply Button Clicked",
      property: window.location.href
    })
  },
  {
    name: "coverageInfoClicked",
    type: "structured",
    makePayload: (params) => {
      const { deviceType, open = true } = params;
      return {
        action: "show_coverage_info_clicked",
        category: `uk-qcp-react-${deviceType}-show-coverage-info-toggle`,
        label: `${open ? "show" : "hide"}_coverage_info_clicked`,
        property: window.location.href
      };
    }
  },
  {
    name: "backToApplication",
    type: "unstructured",
    makePayload: (params) => {
      const { journeyId, vertical } = params;
      return {
        schema: "iglu:com.simplybusiness/qcp_back_to_application/jsonschema/1-0-0",
        data: {
          journey_id: journeyId,
          vertical
        }
      };
    },
    contexts: ["serviceChannelContext"]
  },
  // No quotes referral
  {
    name: "coverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-qcp-react-cover-options-link",
      action: "cover_options_link_clicked",
      label: "cover_options_link_clicked",
      property: window.location.href
    })
  },
  {
    name: "referralReasonsVariantBCoverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-24-12-qcp-mvt-referral-reasons",
      action: "cover_options_link_clicked_variant_b",
      label: "cover_options_link_clicked_variant_b",
      property: window.location.href
    })
  },
  {
    name: "referralReasonsVariantCCoverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-24-12-qcp-mvt-referral-reasons",
      action: "cover_options_link_clicked_variant_c",
      label: "cover_options_link_clicked_variant_c",
      property: window.location.href
    })
  },
  {
    name: "phoneNumberLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-qcp-react-phone-number-link",
      action: "phone_number_link_clicked",
      label: "phone_number_link_clicked",
      property: window.location.href
    })
  },
  {
    name: "linkClicked",
    type: "unstructured",
    makePayload: (params) => {
      const { targetUrl, elementContent } = params;
      return {
        schema: "iglu:com.snowplowanalytics.snowplow/link_click/jsonschema/1-0-1",
        data: {
          targetUrl,
          elementContent
        }
      };
    }
  },
  {
    name: "qcpUpsellAccordionClicked",
    type: "structured",
    makePayload: (params) => ({
      category: "us-qcp-upsells",
      action: "accordion_clicked_qcp",
      label: params?.label,
      property: window.location.href
    })
  },
  {
    name: "qdsUpsellAccordionClicked",
    type: "structured",
    makePayload: (params) => ({
      category: "us-qcp-upsells",
      action: "accordion_clicked_slider",
      label: params?.label,
      property: window.location.href
    })
  },
  {
    name: "qcpUpsellTooltipHelperClicked",
    type: "structured",
    makePayload: (params) => ({
      category: "us-qcp-upsells",
      action: "tooltip_helper_clicked_qcp",
      label: params?.label,
      property: window.location.href
    })
  },
  {
    name: "qdsUpsellTooltipHelperClicked",
    type: "structured",
    makePayload: (params) => ({
      category: "us-qcp-upsells",
      action: "tooltip_helper_clicked_slider",
      label: params?.label,
      property: window.location.href
    })
  },
  {
    name: "qcpQuoteButtonClicked",
    type: "structured",
    makePayload: (payload) => ({
      category: "quote-buttons",
      action: "button-clicked",
      label: payload?.label,
      property: window.location.href
    })
  },
  {
    name: "layoutToggleClickedExperiment",
    type: "structured",
    makePayload: (params) => {
      const { layout } = params;
      return {
        category: "uk-25-04-16-gridview_layout",
        action: "layout_toggle_clicked",
        label: layout,
        property: window.location.href
      };
    }
  },
  {
    name: "selectCoverOptionsClicked",
    type: "structured",
    makePayload: (payload) => {
      return {
        category: "uk-25-09-qcp-covers-demand-test",
        action: "cover_options_clicked",
        label: payload?.label,
        property: window.location.href
      };
    }
  },
  {
    name: "voucherClaimClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-25-06-hyrdra-voucher-intervention-qcp-page",
      action: "voucher_claim_clicked",
      label: "voucher_claim_clicked",
      property: window.location.href
    })
  }
];

// src/snowplow/event-definitions/questionnaire/questionnaire.ts
var questionnaireEventDefinitions = [
  {
    name: "questionAnswered",
    type: "unstructured",
    makePayload: (params) => {
      const { section, context } = params;
      const { question, answer } = redact(params);
      const flatAnswer = Array.isArray(answer) ? answer.join(DELIMITER) : answer;
      return {
        schema: "iglu:com.simplybusiness/form_question_answered/jsonschema/1-0-4",
        data: {
          site: context.site,
          page_index: 1,
          page_name: "quick_to_quote",
          submitted_from: "quick_to_quote_experiment",
          section_name: section,
          question,
          answer: flatAnswer,
          vertical: context.vertical
        }
      };
    }
  },
  {
    name: "primaryDetailSelected",
    type: "unstructured",
    makePayload: (params) => {
      const { context, answer, vertical } = params;
      const { site } = context;
      let verticalName = vertical || context.vertical;
      if (verticalName.toLowerCase().indexOf("landlord") > -1) {
        verticalName = answer === "residential" ? "Landlord" : "Commercial landlord";
      }
      return {
        schema: "iglu:com.simplybusiness/primary_detail_selected/jsonschema/1-3-1",
        data: {
          site,
          vertical: verticalName,
          primary_detail: answer,
          selected_type: "trade_selector"
        }
      };
    },
    contexts: ["distributionChannelContext", "serviceChannelContext"]
  },
  {
    name: "helpTextOpened",
    type: "unstructured",
    makePayload: (params) => {
      const { primaryText, label, context, helpText } = params;
      return {
        schema: "iglu:com.simplybusiness/help_text_opened/jsonschema/1-1-0",
        data: {
          vertical: context.vertical,
          site: context.site,
          primary_text: primaryText,
          help_text: helpText,
          label,
          page_name: "quick_to_quote"
        }
      };
    }
  },
  {
    name: "assumptionsButtonClicked",
    type: "structured",
    makePayload: (params) => {
      const { source, presentationGroup } = params;
      return {
        action: source,
        category: presentationGroup
      };
    }
  },
  {
    name: "navButtonClicked",
    type: "structured",
    makePayload: (params) => {
      const { label, category } = params;
      return {
        label,
        action: "link_click",
        category
      };
    }
  },
  {
    name: "manualAddressClicked",
    type: "structured",
    makePayload: (params) => {
      const { category } = params;
      return {
        action: "link_click",
        category,
        label: "Enter address manually"
      };
    }
  },
  {
    name: "assumptionLinkClicked",
    type: "structured",
    makePayload: (params) => {
      const { label, category, property } = params;
      return {
        action: "assumption_link_click",
        category,
        label,
        property
      };
    }
  },
  {
    name: "addressLookupFocused",
    type: "structured",
    makePayload: (params) => {
      const { label, category } = params;
      return {
        action: "address_lookup_focused",
        category,
        label
      };
    }
  },
  {
    name: "addressLookupTypedIn",
    type: "structured",
    makePayload: (params) => {
      const { label, category } = params;
      return {
        action: "address_lookup_typed_in",
        category,
        label
      };
    }
  },
  {
    name: "addressOptionsDisplayed",
    type: "structured",
    makePayload: (params) => {
      const { label, category } = params;
      return {
        action: "address_options_displayed",
        category,
        label
      };
    }
  },
  {
    name: "tradeChanged",
    type: "structured",
    makePayload: (params) => {
      const { label, property } = params;
      return {
        action: "change_your_selected_profession_button_clicked",
        category: "change_your_selected_profession_button",
        label: `Question: ${label}`,
        property
        // Answer before change
      };
    }
  },
  {
    name: "stateQuestionAnswered",
    type: "unstructured",
    makePayload: (params) => {
      const { context, answer, selectedCoverages } = params;
      const data = {
        vertical: context.vertical,
        selected_type: context.journeyName ?? "usa",
        location: window?.location?.pathname ?? "",
        url: window?.location?.href ?? "",
        state: answer,
        selection_method: "dropdown"
      };
      if (selectedCoverages?.length) {
        data.products = selectedCoverages.join(",");
      }
      return {
        schema: "iglu:com.simplybusiness/state_question_answered/jsonschema/1-0-0",
        data
      };
    }
  }
];

// src/snowplow/event-definitions/referral.ts
var referralEventDefinitions = [
  {
    name: "nextCoverageClicked",
    type: "structured",
    makePayload: (params) => {
      const { url } = params;
      const eventName = "post_referral_workers_compensation_continuation_link";
      return {
        category: eventName,
        action: "link_click",
        label: eventName,
        property: url
      };
    }
  }
];

// src/snowplow/event-definitions/index.ts
var eventDefinitions = [
  ...baseEventDefinitions,
  ...qcpEventDefinitions,
  ...referralEventDefinitions,
  ...interventionEventDefinitions,
  ...questionnaireEventDefinitions,
  ...personalisedCoverEventDefinitions
];
var qcpPageEvents = [...baseEventDefinitions, ...qcpEventDefinitions];
var referralPageEvents = [
  ...baseEventDefinitions,
  ...referralEventDefinitions
];
var personalisedCoverPageEvents = [
  ...baseEventDefinitions,
  ...personalisedCoverEventDefinitions
];
var interventionPageEvents = [...interventionEventDefinitions];
var questionnairePageEvents = [...questionnaireEventDefinitions];

// src/snowplow/getSnowplowConfig.ts
var getSnowplowConfig = (pageData) => pageData.scripts?.find(({ metadata }) => metadata.name === "snowplow")?.props;

// src/snowplow/Snowplow.ts
import {
  newTracker,
  setCookiePath,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
  trackStructEvent
} from "@snowplow/browser-tracker";
var Snowplow = class _Snowplow {
  constructor(props) {
    this.avalancheTrackerName = "sb-ava";
    this.bronzeAvalancheTrackerName = "sb-ava-br";
    this.pvAvalancheTrackerName = "sb-ava-pv";
    this.uid = "";
    this.trackPageView = false;
    this.contexts = {};
    this.pvContext = [];
    this.structContext = [];
    this.serverData = {};
    this.eventHandlers = {};
    if (!props) return;
    const {
      appId,
      cookieDomain,
      avalancheCollector,
      eventMethod,
      uid,
      postPath,
      trackPageView: tpv
    } = props;
    this.uid = uid;
    this.trackPageView = tpv;
    this.serverData = props?.pageViewContext?.data || {};
    const stateStorageStrategy = "cookieAndLocalStorage";
    const baseOptions = {
      appId,
      cookieDomain,
      eventMethod,
      stateStorageStrategy,
      postPath
    };
    newTracker(this.avalancheTrackerName, avalancheCollector, baseOptions);
    newTracker(
      this.bronzeAvalancheTrackerName,
      avalancheCollector,
      baseOptions
    );
    newTracker(this.pvAvalancheTrackerName, avalancheCollector, {
      ...baseOptions,
      eventMethod: eventMethod === "post" ? "beacon" : eventMethod
    });
    setCookiePath("/");
    if (uid) {
      setUserId(uid);
    }
    _Snowplow.instance = this;
  }
  setContexts(contexts) {
    this.contexts = updateIdentityContext(contexts, this.uid);
    this.pvContext = Object.values(this.contexts);
    this.structContext = makeContexts(
      ["distributionChannelContext", "serviceChannelContext"],
      this.contexts
    );
    return this;
  }
  // Send a page view event
  trackView(event) {
    if (this.trackPageView) {
      trackPageView({ ...event, context: this.pvContext }, [
        this.avalancheTrackerName
      ]);
    }
    return this;
  }
  // Send a structured event with contexts
  trackEvent(event) {
    trackStructEvent({ ...event, context: this.structContext }, [
      this.bronzeAvalancheTrackerName
    ]);
    return this;
  }
  // Send a custom event with defined schema and optional contexts
  trackUnstructEvent(event, contexts) {
    if (!event) {
      return this;
    }
    if (contexts && Array.isArray(contexts) && contexts.length > 0) {
      const context = makeContexts(contexts, this.contexts);
      trackSelfDescribingEvent({ event, context }, [this.avalancheTrackerName]);
    } else {
      trackSelfDescribingEvent({ event }, [this.avalancheTrackerName]);
    }
    return this;
  }
  addEventHandlers(eventDefinitions2) {
    const context = this.serverData;
    eventDefinitions2.forEach(({ name, type, makePayload, contexts }) => {
      if (type === "structured") {
        this.addEventHandler(name, (params) => {
          void this.trackEvent(
            makePayload({
              ...params,
              context
            })
          );
        });
      } else {
        this.addEventHandler(name, (params) => {
          const payload = makePayload({
            ...params,
            context
          });
          if (contexts && Array.isArray(contexts) && contexts.length > 0) {
            void this.trackUnstructEvent(payload, contexts);
          } else {
            void this.trackUnstructEvent(payload);
          }
        });
      }
    });
    return this;
  }
  addEventHandler(name, handler) {
    this.eventHandlers[name] = handler;
    return this;
  }
  removeEventHandler(name) {
    delete this.eventHandlers[name];
    return this;
  }
  trigger(name, params) {
    const handler = this.eventHandlers[name];
    if (Object.prototype.hasOwnProperty.call(this.eventHandlers, name) && typeof handler === "function") {
      handler(params);
    }
    return this;
  }
  static getInstance(props) {
    if (!_Snowplow.instance) {
      _Snowplow.instance = new _Snowplow(props);
    }
    return _Snowplow.instance;
  }
};

// src/snowplow/SnowplowContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { jsx } from "react/jsx-runtime";
var SnowplowContext = createContext(null);
var SnowplowProvider = ({ scripts, children }) => {
  const [config, _setConfig] = useState(scripts);
  const snowplow = useRef(Snowplow.getInstance(config));
  useEffect(() => {
    if (snowplow.current && scripts) {
      const contexts = getContexts(config);
      snowplow.current.setContexts(contexts).addEventHandlers(eventDefinitions);
      if (config.trackPageView) {
        void snowplow.current.trackView();
      }
    }
  }, [config, snowplow, scripts]);
  const value = useMemo(
    () => ({
      config,
      snowplow: snowplow.current
    }),
    [config, snowplow]
  );
  return /* @__PURE__ */ jsx(SnowplowContext.Provider, { value, children });
};
function useSnowplowContext() {
  const context = useContext(SnowplowContext);
  if (!context) {
    throw new Error(
      "useSnowplowContext must be used inside a `SnowplowProvider`"
    );
  }
  return context;
}
export {
  AddressLookup,
  Snowplow,
  SnowplowProvider,
  eventDefinitions,
  getAirbrake,
  getContexts,
  getSnowplowConfig,
  interventionPageEvents,
  makeContexts,
  personalisedCoverPageEvents,
  qcpPageEvents,
  questionnairePageEvents,
  referralPageEvents,
  updateIdentityContext,
  useSnowplowContext
};
